import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { defaultStages, setStages, type StageDef, type StageField, type StageKind } from '@/lib/lifecycle-stage-cache'

const KEY = ['cdp', 'lifecycle', 'stages']

/** Giai đoạn vòng đời do từng doanh nghiệp tự đặt (thứ tự = thứ tự phễu). */
export function useLifecycleStages() {
  // Chỉ để nhận biết đổi ngôn ngữ — bộ mặc định (chưa tải xong / tổ chức chưa tự cấu hình) có nhãn là getter theo ngôn ngữ.
  const { i18n: i18nInst } = useTranslation()
  const q = useQuery<StageDef[]>({
    queryKey: KEY,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await api.get<{ stages: StageDef[] }>('/cdp/lifecycle/stages')
      setStages(data.stages)
      return data.stages
    },
  })
  const stages = q.data ?? defaultStages()
  return useMemo(() => {
    const labels: Record<string, string> = Object.fromEntries(stages.map((s) => [s.key, s.label]))
    const colors: Record<string, string | null> = Object.fromEntries(stages.map((s) => [s.key, s.color]))
    return { stages, keys: stages.map((s) => s.key), labels, colors, isLoading: q.isLoading }
  }, [stages, q.isLoading, i18nInst.language])
}

export interface StageInput {
  key?: string
  label: string
  color?: string | null
  kind?: StageKind
  fields?: StageField[]
}

export function useSaveLifecycleStages() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (stages: StageInput[]) => (await api.put<{ stages: StageDef[] }>('/cdp/lifecycle/stages', { stages })).data.stages,
    onSuccess: (stages) => {
      setStages(stages)
      qc.setQueryData(KEY, stages)
      qc.invalidateQueries({ queryKey: ['cdp', 'lifecycle'] })
    },
  })
}
